import { FC, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import jwt_decode from "jwt-decode"

import {
  IDecodedToken,
  refreshToken,
  removeUser,
  tokenToUser,
  updateUserToken,
} from "../../../services/userService"
import { useAuth } from "../../../contexts/auth2"
import http from "../../../services/http-common"
import { FullScreenLoader } from "../../../components/ui"
import { handlePromise } from "../../../utils/handlePromisse"

const ALLOWED_PREFIX = "/dashboard/"

const sanitizeRedirect = (raw: string | null): string | null => {
  if (!raw) return null
  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    return null
  }
  if (decoded.startsWith("//")) return null
  if (decoded.includes("://")) return null
  if (!decoded.startsWith(ALLOWED_PREFIX)) return null
  return decoded
}

const isJwtExpired = (token: string): boolean => {
  try {
    const decoded = jwt_decode<IDecodedToken>(token)
    return !decoded.exp || decoded.exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

const setAuthHeader = (token: string) => {
  http.defaults.headers = {
    "Content-type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

const AuthRedirectPage: FC = () => {
  const navigate = useNavigate()
  const { setUser } = useAuth()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    const target = sanitizeRedirect(params.get("to"))

    const fallbackToLogin = () => {
      removeUser()
      const qs = target ? `?returnTo=${encodeURIComponent(target)}` : ""
      navigate(`/login${qs}`, { replace: true })
    }

    if (!token || !target || isJwtExpired(token)) {
      fallbackToLogin()
      return
    }

    (async () => {
      updateUserToken(token)
      setAuthHeader(token)

      const [result, error] = await handlePromise(refreshToken(token))
      // @ts-ignore — backend returns { newToken }, type in userService says { token }
      const refreshed: string | undefined = result?.data?.newToken
      if (error || !refreshed) {
        fallbackToLogin()
        return
      }

      updateUserToken(refreshed)
      setAuthHeader(refreshed)
      setUser(tokenToUser())
      navigate(target, { replace: true })
    })()
  }, [])

  return <FullScreenLoader message="Autenticando..." />
}

export default AuthRedirectPage